
import React from 'react';
import { Poll } from '@/types/poll';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Globe, Calendar, ChartBar, Hash, ArrowRight } from 'lucide-react';
import { useCountry } from '@/contexts/country'; 
import { format } from 'date-fns';
import useCategories from '@/hooks/useCategories';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LikeButton from '@/components/social/LikeButton';
import ShareButton from '@/components/social/ShareButton';
import { MessageSquare } from 'lucide-react';
import { usePollSocialMetrics } from '@/hooks/usePollSocialMetrics';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import TrendingBadge from '@/components/social/TrendingBadge';

interface PollsInfiniteListProps {
  polls: Poll[];
  emptyMessage?: string;
  hasMore: boolean;
  isLoading: boolean;
  loadMore: () => void;
  lastItemRef: (node: HTMLElement | null) => void;
}

const PollsInfiniteList = ({ 
  polls, 
  emptyMessage = "No polls found matching your criteria.",
  hasMore,
  isLoading,
  loadMore,
  lastItemRef
}: PollsInfiniteListProps) => {
  const { availableCountries } = useCountry();
  const { categories } = useCategories();
  
  if (polls.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        {emptyMessage}
      </div>
    );
  }
  
  const getCountryName = (code: string | null) => {
    if (!code) return 'Global';
    const country = availableCountries.find(c => c.code === code);
    return country ? country.name : code;
  };

  const getCategoryName = (categoryId: string | undefined) => {
    if (!categoryId) return '';
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : '';
  };
  
  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  // Use consistent poll routing - always use /poll/:id to avoid conflicts
  const getPollUrl = (poll: Poll) => {
    return `/poll/${poll.id}`;
  };
  
  return (
    <div className="space-y-4">
      {polls.map((poll, index) => {
        // Get poll URL for this specific poll
        const pollUrl = getPollUrl(poll);
        
        // Apply ref to last item
        const isLastItem = index === polls.length - 1;
        
        return (
          <Card 
            key={poll.id} 
            className="overflow-hidden transition-all hover:shadow-md"
            ref={isLastItem ? lastItemRef : undefined}
          >
            <div className="flex flex-col md:flex-row">
              {/* Image container - only show if poll has an image */}
              {poll.image_url && (
                <div className="w-full md:w-1/3 lg:w-1/4">
                  <div className="h-full">
                    <AspectRatio ratio={3/2} className="h-full">
                      <img
                        src={poll.image_url}
                        alt={poll.title}
                        className="object-cover w-full h-full"
                      />
                    </AspectRatio>
                  </div>
                </div>
              )}
              
              {/* Content container */}
              <div className={`w-full ${poll.image_url ? 'md:w-2/3 lg:w-3/4' : 'w-full'}`}>
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap gap-2 mb-1 items-center">
                    {poll.poll_number && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 font-mono flex items-center gap-1">
                        <Hash className="h-3 w-3 mr-1" />
                        {poll.poll_number}
                      </Badge>
                    )}
                    
                    {poll.status === 'live' ? (
                      <Badge className="bg-green-500">Active</Badge>
                    ) : poll.status === 'closed' ? (
                      <Badge className="bg-gray-500">Closed</Badge>
                    ) : (
                      <Badge variant="outline">Draft</Badge>
                    )}
                    
                    <Badge variant="outline" className="flex items-center">
                      <Globe className="h-3 w-3 mr-1" />
                      {getCountryName(poll.country_code)}
                    </Badge>
                    
                    {poll.category_id && (
                      <Badge variant="secondary">{getCategoryName(poll.category_id)}</Badge>
                    )}
                    
                    {poll.closes_at && (
                      <Badge variant="outline" className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        Closes: {formatDate(poll.closes_at)}
                      </Badge>
                    )}
                    
                    {/* Add trending badge to the badge row */}
                    <PollTrendingBadge pollId={poll.id} />
                  </div>
                  
                  <Link to={pollUrl}>
                    <CardTitle className="text-lg hover:text-blue-600 transition-colors">
                      {poll.title}
                    </CardTitle>
                  </Link>
                </CardHeader>
                
                <CardContent>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {poll.description}
                  </p>
                  
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <ChartBar className="h-4 w-4 mr-1" />
                      <span>{poll.total_votes || 0} responses</span>
                    </div>
                  </div>
                  
                  {/* Social interaction bar - without trending badge since it's in the header */}
                  <div className="flex flex-wrap justify-between items-center pt-3 border-t border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-2">
                      <PollSocialMetrics pollId={poll.id} url={pollUrl} />
                    </div>
                    
                    <Link to={pollUrl}>
                      <Button variant="ghost" size="sm" className="text-blue-600">
                        View Details <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </div>
            </div>
          </Card>
        );
      })}

      <InfiniteScroll 
        isLoading={isLoading} 
        hasMore={hasMore}
        loadMore={loadMore}
        loadingText="Loading more polls..."
        endMessage="You've reached the end of the polls"
      />
    </div>
  );
};

// Extract the trending badge to a separate component for cleaner code
const PollTrendingBadge = ({ pollId }: { pollId: string }) => {
  const { metrics } = usePollSocialMetrics(pollId);
  
  if (!metrics || metrics.heatScore < 50) return null;
  
  return <TrendingBadge score={metrics.heatScore} size="sm" />;
};

// Extract the social metrics to a separate component for cleaner code
const PollSocialMetrics = ({ pollId, url }: { pollId: string, url: string }) => {
  const { metrics } = usePollSocialMetrics(pollId);
  
  return (
    <>
      <LikeButton 
        cardId={pollId}
        initialLikes={metrics?.likesCount || 0} 
        showCount={true}
        size="sm"
        contentType="poll"
      />
      
      <Link to={`${url}#comments`}>
        <Button variant="outline" size="sm">
          <MessageSquare className="h-4 w-4 mr-1" />
          {metrics?.commentsCount || 0}
        </Button>
      </Link>
      
      <ShareButton
        contentId={pollId}
        contentType="poll"
        initialShares={metrics?.sharesCount || 0}
        showCount={true}
        size="sm"
        url={`${window.location.origin}${url}`}
        title={`Poll`}
      />
    </>
  );
};

export default PollsInfiniteList;
